"use client";

import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const DEFAULT_EMAIL_REGEX =
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface EmailSelectorV2Props {
  /** Lista controlada de correos válidos. */
  value: string[];
  /** Se dispara con la lista actualizada de correos válidos. */
  onChange: (emails: string[]) => void;
  placeholder?: string;
  /** Validador opcional; por defecto una regex de email estándar. */
  validateEmail?: (email: string) => boolean;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
}

/**
 * Selector de múltiples correos en lenguaje v2 — reemplazo de `react-multi-email`.
 *
 * - Escribe un correo y confirma con Enter, coma, espacio o `;` (o al perder foco).
 * - Pega una lista (separada por coma/espacio/salto de línea) y se trocea sola.
 * - Backspace con el input vacío elimina el último chip.
 * - Los correos inválidos se descartan y se reflejan en `invalid` (feedback visual
 *   temporal en el propio input).
 */
export function EmailSelectorV2({
  value,
  onChange,
  placeholder,
  validateEmail,
  disabled = false,
  className,
  "aria-label": ariaLabel,
}: EmailSelectorV2Props) {
  const [draft, setDraft] = React.useState("");
  const [focused, setFocused] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const isValid = React.useCallback(
    (email: string) =>
      validateEmail ? validateEmail(email) : DEFAULT_EMAIL_REGEX.test(email),
    [validateEmail]
  );

  const commit = React.useCallback(
    (raw: string): boolean => {
      const candidates = raw
        .split(/[\s,;]+/)
        .map((e) => e.trim())
        .filter(Boolean);

      if (candidates.length === 0) return true;

      const valid: string[] = [];
      let anyInvalid = false;
      candidates.forEach((email) => {
        if (isValid(email) && !value.includes(email) && !valid.includes(email)) {
          valid.push(email);
        } else if (!isValid(email)) {
          anyInvalid = true;
        }
      });

      if (valid.length > 0) onChange([...value, ...valid]);
      setHasError(anyInvalid);
      return !anyInvalid;
    },
    [isValid, onChange, value]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["Enter", ",", ";", " ", "Tab"].includes(e.key)) {
      if (draft.trim()) {
        e.preventDefault();
        if (commit(draft)) setDraft("");
      }
      return;
    }
    if (e.key === "Backspace" && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    if (/[\s,;]/.test(text)) {
      e.preventDefault();
      if (commit(text)) setDraft("");
    }
  };

  const handleBlur = () => {
    setFocused(false);
    if (draft.trim() && commit(draft)) setDraft("");
  };

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
    inputRef.current?.focus();
  };

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className={cn(
        "flex min-h-[52px] w-full flex-wrap items-center gap-2 rounded-xl border bg-white px-3 py-2 text-sm transition-colors dark:bg-zinc-900",
        focused
          ? "border-indigo-400 ring-2 ring-indigo-400/30"
          : hasError
          ? "border-red-400"
          : "border-slate-200 dark:border-zinc-800",
        disabled && "cursor-not-allowed opacity-60",
        className
      )}
    >
      {value.map((email, index) => (
        <span
          key={`${email}-${index}`}
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 py-1 pl-2.5 pr-1.5 text-xs font-medium text-slate-700 dark:bg-zinc-800 dark:text-zinc-200"
        >
          {email}
          <button
            type="button"
            tabIndex={-1}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              removeAt(index);
            }}
            aria-label={`Quitar ${email}`}
            className="flex h-4 w-4 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-zinc-700 dark:hover:text-zinc-100"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        disabled={disabled}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          if (hasError) setHasError(false);
        }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        placeholder={value.length === 0 ? placeholder : ""}
        aria-label={ariaLabel ?? "Email selector"}
        className="min-w-[140px] flex-1 border-0 bg-transparent p-1 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-0 dark:text-zinc-100 dark:placeholder:text-zinc-500"
      />
    </div>
  );
}
